import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Box, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Edge, Node, NodeProps, ReactFlow, ReactFlowProvider, useReactFlow, useStore} from 'reactflow';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {EdgesContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/EdgesContext';
import {FlowchartEditorModeContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartEditorModeContext';
import {
  FlowchartFeaturesContext,
  getValueForFlowchartFeaturesContext,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {HandlesContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/HandlesContext';
import {NodesContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/NodesContext';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  ExecutionFlowchartRuntimeContext,
  type ExecutionFlowchartRuntimeValue,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartRuntimeContext';
import {
  DEFAULT_FLOWCHART_PROPS,
  EXECUTION_VIEWER_FLOWCHART_PROPS,
  FlowchartEdge,
  FlowchartStaticEdgeData,
  ProcessFlowchartNodeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorRegistry';
import {TransitionsContextProvider} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {useNodeDimensions} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useNodeDimensions';
import {ensureDefaultProcessesPluginRegistered} from 'processes/settings/ProcessTaskSchemaEditor';
import HorizontalControls, {FIT_VIEW_MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {EdgeType} from 'ui-library/reactflow/edges/edgeOptions';
import {useGetNodeHandlesData} from 'ui-library/reactflow/handles/useGetNodeHandlesData';
import {LayoutAlgorithmName} from 'ui-library/reactflow/layout/algorithms';
import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';

import RouterNodeRenderer from './horizontal-flowchart/nodes/RouterNodeRenderer';
import StopNodeRenderer from './horizontal-flowchart/nodes/StopNodeRenderer';
import type {ViewerStaticFlowchartNodeData, ViewerStaticTemplateFlowchart} from './ReadOnlyFlowchartTypes';
import ViewerTaskSchemaNodeRenderer from './ViewerTaskSchemaNodeRenderer';

export interface ReadOnlyFlowchartProps {
  flowchart: ViewerStaticTemplateFlowchart;
  onNodeNavigateClick?: (nodeConfigId: string) => void;
  selectedNodeConfigId?: string;
}

const useStyles = createStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  reactFlow: {
    backgroundColor: theme.colors.gray[0],
  },
}));

const EMPTY_FEATURES = new Map<ProcessFlowchartPlatformFeatureType, boolean>();
const EMPTY_TRANSITIONS = {transitionsById: {}, ptdvPairToTransitionIdIndex: {}};

const VIEWER_NODE_TYPES: Record<string, React.ComponentType<NodeProps>> = {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: ViewerTaskSchemaNodeRenderer,
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNodeRenderer,
  [ProcessFlowchartNodeType.STOP_NODE]: StopNodeRenderer,
};

type ViewerNode = Node<
  ViewerStaticFlowchartNodeData & {
    nodeDetailsSidepanelContent: Array<unknown>;
    onFieldClick: () => void;
    onUpdateNodeData: () => Promise<null>;
  }
>;

function buildViewerNode(staticData: ViewerStaticFlowchartNodeData): ViewerNode {
  return {
    id: staticData.id,
    type: staticData.nodeType,
    position: {x: 0, y: 0},
    data: {
      ...staticData,
      nodeDetailsSidepanelContent: [],
      onFieldClick: noop,
      onUpdateNodeData: () => Promise.resolve(null),
    },
  };
}

function buildNodes(staticNodes: Array<ViewerStaticFlowchartNodeData>): Array<ViewerNode> {
  return staticNodes.map((staticData) => buildViewerNode(staticData));
}

function buildEdges(staticEdges: Array<FlowchartStaticEdgeData>): Array<FlowchartEdge> {
  return staticEdges.map((edge) => ({
    ...edge,
    data: {type: EdgeType.ELK_SPLINE, elkEdgeData: undefined},
  }));
}

function ReadOnlyFlowchartCanvas(props: ReadOnlyFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const reactFlowInstance = useReactFlow();

  const [nodes, setNodes] = useState<Array<ViewerNode>>(() => buildNodes(props.flowchart.nodesStaticData));
  const [edges, setEdges] = useState<Array<FlowchartEdge>>(() => buildEdges(props.flowchart.edgesStaticData));
  const hasPerformedLayout = useRef(false);
  const [layoutDone, setLayoutDone] = useState(false);

  const {getMeasuredDimensionsFromStore} = useNodeDimensions();

  useEffect(() => {
    setNodes(buildNodes(props.flowchart.nodesStaticData));
    setEdges(buildEdges(props.flowchart.edgesStaticData));
    hasPerformedLayout.current = false;
    setLayoutDone(false);
  }, [props.flowchart]);

  const getHandles = useGetNodeHandlesData();

  const allNodesReady = useStore(
    useCallback(() => {
      if (hasPerformedLayout.current || nodes.length === 0) {
        return false;
      }
      return nodes.every((node) => getHandles(node.id).length > 0);
    }, [nodes, getHandles])
  );

  useEffect(() => {
    if (!allNodesReady || hasPerformedLayout.current || nodes.length === 0) {
      return;
    }

    const layoutSettings: Partial<LayoutSettings<ViewerNode, Edge>> = {
      algorithm: LayoutAlgorithmName.ELK,
      elkDirection: 'DOWN',
      calculateNodeDimensions: (node) => getMeasuredDimensionsFromStore(node.id),
      horizontalSpaceBetweenNodes: 30,
    };

    layoutFlowchart({nodes, edges, getHandles}, layoutSettings).then(
      ({nodes: layoutNodes, edges: layoutEdges}) => {
        setNodes(layoutNodes);
        setEdges(layoutEdges);
        hasPerformedLayout.current = true;
        setLayoutDone(true);
      }
    );
  }, [allNodesReady, nodes, edges, getHandles, getMeasuredDimensionsFromStore]);

  useEffect(() => {
    if (!layoutDone) {
      return;
    }
    setTimeout(() => {
      reactFlowInstance.fitView({...DEFAULT_FIT_VIEW_OPTIONS, maxZoom: FIT_VIEW_MAX_ZOOM});
    });
    setLayoutDone(false);
  }, [layoutDone, reactFlowInstance]);

  const nodesToRender = useMemo(() => {
    if (hasPerformedLayout.current) {
      return nodes;
    }
    return nodes.map((node) => hideFlowchartElementBeforeLayout(node));
  }, [nodes, layoutDone]); // eslint-disable-line react-hooks/exhaustive-deps

  const edgesToRender = useMemo(() => {
    if (hasPerformedLayout.current) {
      return edges;
    }
    return edges.map((edge) => hideFlowchartElementBeforeLayout(edge));
  }, [edges, layoutDone]); // eslint-disable-line react-hooks/exhaustive-deps

  const flowchartFeatures = useMemo(
    () => getValueForFlowchartFeaturesContext(EMPTY_FEATURES, FlowchartEditorMode.EXECUTION_VIEWER),
    []
  );

  const nodesContext = useMemo(
    () => ({nodes: nodes as Array<never>, startNode: null, endNode: null}),
    [nodes]
  );
  const edgesContext = useMemo(() => ({edges, onEdgeRemove: noop}), [edges]);
  const handlesContext = useMemo(() => ({connectionDragState: null, hideDataFlowEdges: true}), []);

  const runtimeContextValue: Pick<
    ExecutionFlowchartRuntimeValue,
    'onNodeNavigateClick' | 'selectedNodeConfigId'
  > | null = useMemo(() => {
    if (props.onNodeNavigateClick == null) {
      return null;
    }
    return {
      onNodeNavigateClick: props.onNodeNavigateClick,
      selectedNodeConfigId: props.selectedNodeConfigId,
    };
  }, [props.onNodeNavigateClick, props.selectedNodeConfigId]);

  // FlowchartEditorModeContext + ReactFlowLockedContext: used by RouterNodeRenderer/StopNodeRenderer.
  // ExecutionFlowchartRuntimeContext: used by ViewerTaskSchemaNodeRenderer for navigation.
  // Others are defensive — shared node renderers may call useContext without a guard.
  return (
    <FlowchartEditorModeContext.Provider value={FlowchartEditorMode.EXECUTION_VIEWER}>
      <ReactFlowLockedContext.Provider value={false}>
        <TransitionsContextProvider value={EMPTY_TRANSITIONS}>
          <ExecutionFlowchartRuntimeContext.Provider
            value={runtimeContextValue as ExecutionFlowchartRuntimeValue | null}
          >
            <FlowchartFeaturesContext.Provider value={flowchartFeatures}>
              <HandlesContext.Provider value={handlesContext}>
                <NodesContext.Provider value={nodesContext}>
                  <EdgesContext.Provider value={edgesContext}>
                    <ReactFlow
                      {...defaultReactFlowProps}
                      {...DEFAULT_FLOWCHART_PROPS}
                      {...EXECUTION_VIEWER_FLOWCHART_PROPS}
                      className={classes.reactFlow}
                      nodes={nodesToRender}
                      nodeTypes={VIEWER_NODE_TYPES}
                      edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
                      edges={edgesToRender}
                      deleteKeyCode={null}
                      onlyRenderVisibleElements={true}
                    >
                      <HorizontalControls />
                    </ReactFlow>
                  </EdgesContext.Provider>
                </NodesContext.Provider>
              </HandlesContext.Provider>
            </FlowchartFeaturesContext.Provider>
          </ExecutionFlowchartRuntimeContext.Provider>
        </TransitionsContextProvider>
      </ReactFlowLockedContext.Provider>
    </FlowchartEditorModeContext.Provider>
  );
}

/**
 * A lightweight, read-only flowchart viewer for execution/lineage contexts.
 * Renders a static vertical flowchart from pre-built structure data without
 * loading the full config editor draft state or plugin infrastructure.
 */
export default function ReadOnlyFlowchart(props: ReadOnlyFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  ensureDefaultProcessesPluginRegistered({ignoreFlowchartConfigFlag: true});

  return (
    <Box className={classes.root} {...getTestProps({component: 'ReadOnlyFlowchart', element: 'root'})}>
      <ReactFlowProvider>
        <ReactFlowWrapper fullWidth={true} deferUntilMeasured={true}>
          <ReadOnlyFlowchartCanvas {...props} />
        </ReactFlowWrapper>
      </ReactFlowProvider>
    </Box>
  );
}
